import { generateOptions } from "@/services/generic.methods";
import { createSlice } from "@reduxjs/toolkit";

/**
 * @name @createSlice
 * @description create redux store slice for Workflow
 **/
export const WFSlice = createSlice({
  name: "workflow",
  initialState: {
    loading: false,
    lists: [],
    listTotalCount: 0,
    listData: {},
    settingData: {},
    WFTemplateData: {},
    WFTemplates: [],
    selectedWFTemplate: {},
    templatesTotalCount: 0,
    data: null,
    serverData: null,
    triggers: [],
    MELCount: 0,
    triggersList: [],
    triggerCategoriesList: [],
  },

  reducers: {
    setLoadingToState: (state, action) => {
      state.loading = action.payload;
    },
    // for Manual leads count update
    setMELCountToState: (state, action) => {
      state.MELCount = action.payload;
    },
    setListsToState: (state, action) => {
      state.lists = action.payload;
    },
    setListTotalCount: (state, action) => {
      state.listTotalCount = action.payload;
    },
    setTemplatesTotalCount: (state, action) => {
      state.templatesTotalCount = action.payload;
    },
    setSelectedWFTemplate: (state, action) => {
      state.selectedWFTemplate = action.payload;
    },
    appendListsToState: (state, action) => {
      const existingIds = new Set(state.lists.map((item) => item.id));
      const newLists = action.payload.filter(
        (item) => !existingIds.has(item.id)
      );
      state.lists = [...state.lists, ...newLists];
    },
    setWFTemplatesToState: (state, action) => {
      state.WFTemplates = action.payload;
    },
    appendTemplatesToState: (state, action) => {
      const existingIds = new Set(state.WFTemplates.map((item) => item.id));
      const newTemps = action.payload.filter(
        (item) => !existingIds.has(item.id)
      );
      state.WFTemplates = [...state.WFTemplates, ...newTemps];
    },
    setListDataToState: (state, action) => {
      state.listData = action.payload;
    },
    setWFTemplateDataToState: (state, action) => {
      state.WFTemplateData = action.payload;
    },
    setSettingDataToState: (state, action) => {
      state.settingData = action.payload;
    },
    setWFServerDataToState: (state, action) => {
      state.serverData = action.payload;
    },
    setTriggersListToState: (state, action) => {
      state.triggersList = action.payload;
    },
    setTriggerCategoriesListToState: (state, action) => {
      state.triggerCategoriesList = action.payload;
    },
  },
});

export const {
  setLoadingToState,
  setListsToState,
  setTemplatesTotalCount,
  setSelectedWFTemplate,
  appendTemplatesToState,
  appendListsToState,
  setListTotalCount,
  setWFTemplatesToState,
  setListDataToState,
  setWFTemplateDataToState,
  setSettingDataToState,
  setWFServerDataToState,
  setMELCountToState,
  setTriggersListToState,
  setTriggerCategoriesListToState,
} = WFSlice.actions;

/**
 * These function called thunk and allows to perform async logic
 * It can be dispatched like a regular action: `dispatch(data)`
 **/
export const getListsAsync =
  (query, payload, saveInStore = true, appendInStore = false) =>
  (dispatch) => {
    if (saveInStore) {
      dispatch(setLoadingToState(true));
      dispatch(setListsToState([]));
    }

    return query(payload)
      .then(async (res) => {
        let { data } = res;
        if (data?.lists && data?.lists?.items) {
          await dispatch(setListTotalCount(data?.lists?.totalCount));
          let lists = generateOptions(data?.lists?.items, "name", "id");
          if (saveInStore) {
            await dispatch(setListsToState(lists));
            dispatch(setLoadingToState(false));
          } else if (appendInStore) {
            await dispatch(appendListsToState(lists));
            dispatch(setLoadingToState(false));
          }

          return Promise.resolve(lists);
        }
        return Promise.reject(res);
      })
      .catch((error) => {
        dispatch(setLoadingToState(false));
        return Promise.reject(error);
      });
  };

export const getWFTemplatesAsync =
  (query, payload, appendInStore = false) =>
  (dispatch) => {
    if (!appendInStore) {
      dispatch(setLoadingToState(true));
      dispatch(setWFTemplatesToState([]));
    }
    return query(payload)
      .then(async (res) => {
        let { data } = res;
        console.log("data", data);
        if (data?.wfTemplatesGrid && data?.wfTemplatesGrid?.items) {
          await dispatch(
            setTemplatesTotalCount(data?.wfTemplatesGrid?.totalCount)
          );
          let templates = generateOptions(
            data?.wfTemplatesGrid?.items,
            "name",
            "id"
          );
          if (appendInStore) {
            await dispatch(appendTemplatesToState(templates));
          } else {
            await dispatch(setWFTemplatesToState(templates));
          }
          dispatch(setLoadingToState(false));
          return Promise.resolve(data);
        }
        return Promise.reject(res);
      })
      .catch((error) => {
        dispatch(setLoadingToState(false));
        return Promise.reject(error);
      });
  };

export const getWFByIdAsync = (query, payload) => (dispatch) => {
  return query(payload)
    .then(async (res) => {
      const { data } = res;
      if (data?.workFlow && data?.workFlow?.items) {
        let workFlow = data?.workFlow.items[0];
        await dispatch(setWFServerDataToState(workFlow));
        return Promise.resolve(workFlow);
      }
      return Promise.reject(res);
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

/* called a selector and allows to select values from the state */
export const getLoading = (state) => {
  return state.workflow?.loading;
};
export const getLists = (state) => {
  return state.workflow?.lists;
};
export const getWFTemplates = (state) => {
  return state.workflow?.WFTemplates;
};
export const getSelectedWFTemplate = (state) => {
  return state.workflow?.selectedWFTemplate;
};

export const getSavedWFData = (state) => {
  return state.workflow?.serverData;
};
export const getMELCount = (state) => {
  return state.workflow?.MELCount;
};
export const getListCount = (state) => {
  return state.workflow?.listTotalCount;
};
export const getTemplatesCount = (state) => {
  return state.workflow?.templatesTotalCount;
};

export const getTriggersList = (state) => {
  return state.workflow?.triggersList;
};
export const getTriggerCategoriesList = (state) => {
  return state.workflow?.triggerCategoriesList;
};

export default WFSlice.reducer;
